import React, { useState, useRef, useCallback, useEffect } from "react";
import { ScanFace, Upload, Link2, X, AlertTriangle, CheckCircle, Loader2, ChevronDown, ChevronUp, Film, Image as ImageIcon, Shield, Zap } from "lucide-react";
import { useMamdani } from "../../context/MamdaniContext";

const API_BASE = "https://api.anti-bot.ai";

const DEMO_CASES = [
  {
    id: 1,
    label: "ביבי נתניהו – קפה",
    type: "video",
    media_url: "https://api.anti-bot.ai/media/bibi_display.mp4",
    verdict: "deepfake",
    deepfake_score: 81.9,
    ai_generated_score: 0.5,
    frame_count: 59,
    scan_id: 1,
    analysis: {
      customer_title: "זוהה Deepfake בוידאו פוליטי",
      customer_summary: "הוידאו זוהה כ-Deepfake ב-81.9% ביטחון. בשניות 32 ו-51 נמצאו artifacts בולטים בתנועת השפתיים שאינה מסתנכרנת עם הקול. זוהי טכניקה נפוצה לייצור וידאו מזויף של דמויות פוליטיות.",
      indicators: ["אי-סנכרון שפתיים עם שמע", "artifacts בזמן תנועת פנים", "טקסטורת עור לא טבעית"],
      face_analysis: { blending_artifacts: true, unnatural_edges: true, lighting_inconsistency: true, texture_anomalies: false },
      risk_level: "high"
    }
  },
  {
    id: 3,
    label: "ביבי ועבאס – תמונה מזויפת",
    type: "image",
    media_url: "https://api.anti-bot.ai/media/bibi_abbas_ai_generated.png",
    verdict: "ai_generated",
    deepfake_score: 0.0,
    ai_generated_score: 100.0,
    frame_count: 1,
    scan_id: 3,
    analysis: {
      customer_title: "תמונה סינתטית – נוצרה במלואה על ידי AI",
      customer_summary: "המערכת זיהתה כי התמונה נוצרה במלואה על ידי כלי בינה מלאכותית גנרטיבי (כגון Midjourney, DALL-E או Stable Diffusion), ולא צולמה במציאות. הציון של 100% ל-AI Generated מעיד על דפוסים ויזואליים אופייניים לתמונות מחולל, כגון מרקם עור חלק ואחיד באופן לא טבעי, תאורה שנראית מושלמת מדי ולא עקבית עם סביבה אמיתית, וקצוות פנים או שיער המופיעים מטושטשים או לא חדים. בנוסף, המערכת לא זיהתה סימני Deepfake קלאסיים (כגון החלפת פנים), מה שמחזק את המסקנה שהתמונה כולה סינתטית מראשיתה.",
      indicators: ["מרקם עור חלק ומלוטש באופן לא טבעי – חסר פגמים, נקבוביות ופרטים ביולוגיים אמיתיים", "תאורה סימטרית ומושלמת מדי שאינה תואמת תנאי צילום טבעיים", "קצוות שיער ופנים מטושטשים או בעלי מעברים לא עקביים עם הרקע"],
      face_analysis: { blending_artifacts: true, unnatural_edges: true, lighting_inconsistency: true, texture_anomalies: true },
      risk_level: "critical"
    }
  },
  {
    id: 2,
    label: "בנט ולפיד – תמונה מזויפת",
    type: "image",
    media_url: "https://api.anti-bot.ai/media/bennett_lapid_ai_generated.png",
    verdict: "ai_generated",
    deepfake_score: 0.2,
    ai_generated_score: 99.6,
    frame_count: 1,
    scan_id: 2,
    analysis: {
      customer_title: "תמונה סינתטית – נוצרה במלואה על ידי AI",
      customer_summary: "המערכת זיהתה בוודאות גבוהה מאוד (99.6%) שהתמונה נוצרה באופן מלא על ידי כלי בינה מלאכותית גנרטיבי כגון Midjourney, DALL-E או Stable Diffusion, ולא צולמה במציאות. בניגוד ל-Deepfake שמשתמש בפנים אמיתיות ומחליף אותן, כאן כל התמונה סינתטית לחלוטין. המערכת זיהתה דפוסים אופייניים ליצירת AI כמו מרקם עור אחיד ומלאכותי, פרטים לא עקביים ברקע, וחוסר בפגמים טבעיים שקיימים בתמונות אמיתיות.",
      indicators: ["מרקם עור חלק ואחיד באופן לא טבעי – ללא נקבוביות, קמטים או פגמים אמיתיים", "דפוסים סינתטיים אופייניים לרשתות GAN/Diffusion זוהו בפיקסלים", "היעדר רעש אורגני וליקויים טבעיים המאפיינים תמונות צילום אמיתיות", "חוסר עקביות בפרטים עדינים כגון שיער, ריסים ורקע"],
      face_analysis: { blending_artifacts: true, unnatural_edges: true, lighting_inconsistency: true, texture_anomalies: true },
      risk_level: "critical"
    }
  }
];

const VERDICT_CONFIG = {
  deepfake:     { label: "Deepfake זוהה",      color: "#E53E3E", bg: "rgba(229,62,62,0.12)",  icon: AlertTriangle },
  ai_generated: { label: "תוכן AI זוהה",       color: "#DD6B20", bg: "rgba(221,107,32,0.12)", icon: AlertTriangle },
  suspicious:   { label: "חשוד – דורש בדיקה",  color: "#D69E2E", bg: "rgba(214,158,46,0.12)", icon: AlertTriangle },
  clean:        { label: "נראה תקין",           color: "#38A169", bg: "rgba(56,161,105,0.12)", icon: CheckCircle },
};

const VERDICT_CONFIG_EN = {
  deepfake:     { label: "Deepfake Detected",   color: "#E53E3E", bg: "rgba(229,62,62,0.12)",  icon: AlertTriangle },
  ai_generated: { label: "AI Content Detected", color: "#DD6B20", bg: "rgba(221,107,32,0.12)", icon: AlertTriangle },
  suspicious:   { label: "Suspicious – Review", color: "#D69E2E", bg: "rgba(214,158,46,0.12)", icon: AlertTriangle },
  clean:        { label: "Appears Clean",        color: "#38A169", bg: "rgba(56,161,105,0.12)", icon: CheckCircle },
};

const RISK_CONFIG = {
  critical: { label: "קריטי",  color: "#E53E3E" },
  high:     { label: "גבוה",   color: "#DD6B20" },
  medium:   { label: "בינוני", color: "#D69E2E" },
  low:      { label: "נמוך",   color: "#38A169" },
};

const RISK_CONFIG_EN = {
  critical: { label: "Critical", color: "#E53E3E" },
  high:     { label: "High",     color: "#DD6B20" },
  medium:   { label: "Medium",   color: "#D69E2E" },
  low:      { label: "Low",      color: "#38A169" },
};

const DEMO_CASES_MAMDANI = [
  {
    id: 'm1',
    label: "Mamdani – AI Image Check",
    type: "image",
    media_url: "/image/mamdani-1.jpeg",
    verdict: "ai_generated",
    deepfake_score: 12.4,
    ai_generated_score: 87.3,
    frame_count: 1,
    scan_id: "m1",
    analysis: {
      customer_title: "AI-Generated Image Detected",
      customer_summary: "This image shows strong indicators of AI generation at 87.3% confidence. The facial texture appears unnaturally smooth, lighting is inconsistent with real-world photography, and background elements exhibit GAN artifacts typical of Midjourney or Stable Diffusion outputs. This type of synthetic imagery is commonly used in disinformation campaigns to fabricate political endorsements or place figures in staged scenarios.",
      indicators: ["Unnaturally smooth facial texture", "Lighting inconsistent with real-world conditions", "Background GAN artifacts detected", "Missing biological imperfections"],
      face_analysis: { blending_artifacts: true, unnatural_edges: true, lighting_inconsistency: true, texture_anomalies: true },
      risk_level: "high"
    }
  },
  {
    id: 'm2',
    label: "Mamdani – Deepfake Check",
    type: "image",
    media_url: "/image/mamdani-2.jpeg",
    verdict: "clean",
    deepfake_score: 8.1,
    ai_generated_score: 5.2,
    frame_count: 1,
    scan_id: "m2",
    analysis: {
      customer_title: "Appears Authentic",
      customer_summary: "This image passed deepfake and AI-generation detection with low risk scores. Deepfake score is 8.1% and AI-generated score is 5.2%, both well below the suspicious threshold. Facial features, lighting, and background are consistent with authentic photography. No evidence of face-swapping or synthetic generation was detected.",
      indicators: ["Natural facial texture detected", "Consistent lighting patterns", "No blending artifacts found"],
      face_analysis: { blending_artifacts: false, unnatural_edges: false, lighting_inconsistency: false, texture_anomalies: false },
      risk_level: "low"
    }
  }
];

function ScoreBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ color: "#B7A99A", fontSize: 12 }}>{label}</span>
        <span style={{ color, fontWeight: 700, fontSize: 12 }}>{value}%</span>
      </div>
      <div style={{ height: 5, background: "rgba(74,63,55,0.3)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 3, transition: "width 1.2s ease" }} />
      </div>
    </div>
  );
}

function FaceAnalysis({ data }) {
  const { mamdaniMode: mm } = useMamdani();
  const items = [
    { key: "blending_artifacts",     label: "Blending Artifacts" },
    { key: "unnatural_edges",        label: mm ? "Unnatural Edges"        : "קצוות לא טבעיים" },
    { key: "lighting_inconsistency", label: mm ? "Lighting Inconsistency" : "תאורה לא עקבית" },
    { key: "texture_anomalies",      label: mm ? "Texture Anomalies"      : "חריגות מרקם" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
      {items.map(({ key, label }) => (
        <div key={key} style={{
          display: "flex", alignItems: "center", gap: 6,
          background: data[key] ? "rgba(229,62,62,0.08)" : "rgba(56,161,105,0.08)",
          border: `1px solid ${data[key] ? "rgba(229,62,62,0.25)" : "rgba(56,161,105,0.25)"}`,
          borderRadius: 8, padding: "5px 8px"
        }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: data[key] ? "#E53E3E" : "#38A169", flexShrink: 0 }} />
          <span style={{ color: "#F3E7D7", fontSize: 11 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function ResultCard({ result, onClose }) {
  const { mamdaniMode: mm } = useMamdani();
  const [showFrames, setShowFrames] = useState(false);
  const [displayedSummary, setDisplayedSummary] = useState(result.animate ? "" : (result.analysis?.customer_summary || ""));
  const vCfg = mm ? VERDICT_CONFIG_EN : VERDICT_CONFIG;
  const rCfg = mm ? RISK_CONFIG_EN : RISK_CONFIG;
  const cfg = vCfg[result.verdict] || vCfg.clean;
  const Icon = cfg.icon;
  const analysis = result.analysis || {};
  const risk = rCfg[analysis.risk_level] || rCfg.medium;

  useEffect(() => {
    if (!result.animate || !analysis.customer_summary) return;
    let i = 0;
    const fullText = analysis.customer_summary;
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayedSummary(fullText.slice(0, i));
        if (i >= fullText.length) clearInterval(interval);
      }, 18);
      return () => clearInterval(interval);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [result.animate, analysis.customer_summary]);

  return (
    <div style={{ background: "#26211D", border: `1px solid ${cfg.color}40`, borderRadius: 16, padding: 24, marginTop: 20, position: "relative" }}>
      {onClose && (
        <button onClick={onClose} style={{ position: "absolute", top: 14, left: 14, background: "rgba(74,63,55,0.3)", border: "none", borderRadius: 6, cursor: "pointer", color: "#B7A99A", padding: 4 }}>
          <X size={16} />
        </button>
      )}

      {/* מדיה */}
      {result.media_url && result.type === "video" && (
        <video src={result.media_url} controls style={{ width: "100%", borderRadius: 12, marginBottom: 16, maxHeight: 280, background: "#000" }} />
      )}
      {result.media_url && result.type === "image" && (
        <img src={result.media_url} alt="media" style={{ width: "100%", borderRadius: 12, marginBottom: 16, maxHeight: 200, objectFit: "contain" }} />
      )}

      {/* Verdict Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: cfg.bg, border: `1px solid ${cfg.color}40`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Icon size={20} color={cfg.color} />
          <div>
            <div style={{ color: cfg.color, fontWeight: 700, fontSize: 15 }}>{analysis.customer_title || cfg.label}</div>
            <div style={{ color: "#B7A99A", fontSize: 11, marginTop: 2 }}>
              {result.scan_id ? `scan #${result.scan_id} • ` : ""}{result.frame_count || 1} {mm ? "frames" : "פריימים"} • {result.type === "video" ? (mm ? "video" : "וידאו") : (mm ? "image" : "תמונה")}
            </div>
          </div>
        </div>
        <span style={{ background: risk.color + "22", color: risk.color, border: `1px solid ${risk.color}44`, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
          {mm ? "Risk:" : "סיכון"} {risk.label}
        </span>
      </div>

      {/* ציונים */}
      <div style={{ marginBottom: 16 }}>
        <ScoreBar label="Deepfake Score" value={result.deepfake_score} color="#E53E3E" />
        <ScoreBar label="AI Generated Score" value={result.ai_generated_score} color="#DD6B20" />
      </div>

      {/* סיכום AI */}
      {analysis.customer_summary && (
        <div style={{ background: "rgba(18,15,13,0.6)", border: "1px solid #4A3F37", borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <div style={{ color: "#D89B5A", fontSize: 12, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <ScanFace size={13} /> {mm ? "AI Analysis" : "ניתוח AI"}
          </div>
          <p style={{ color: "#F3E7D7", fontSize: 13, lineHeight: 1.75, margin: 0 }}>{displayedSummary}</p>
        </div>
      )}

      {/* סימנים */}
      {analysis.indicators?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: "#B7A99A", fontSize: 11, marginBottom: 8, fontWeight: 600 }}>{mm ? "Detected Indicators:" : "סימנים שזוהו:"}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {analysis.indicators.map((ind, i) => (
              <span key={i} style={{ background: "rgba(216,155,90,0.1)", border: "1px solid rgba(216,155,90,0.28)", borderRadius: 20, padding: "3px 10px", color: "#D89B5A", fontSize: 11 }}>{ind}</span>
            ))}
          </div>
        </div>
      )}

      {/* ניתוח פנים */}
      {analysis.face_analysis && Object.keys(analysis.face_analysis).length > 0 && (
        <div>
          <div style={{ color: "#B7A99A", fontSize: 11, marginBottom: 8, fontWeight: 600 }}>{mm ? "Face Analysis:" : "ניתוח פנים:"}</div>
          <FaceAnalysis data={analysis.face_analysis} />
        </div>
      )}

      {/* פריימים */}
      {result.frames?.length > 1 && (
        <div style={{ marginTop: 14 }}>
          <button onClick={() => setShowFrames(!showFrames)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #4A3F37", borderRadius: 8, padding: "5px 12px", cursor: "pointer", color: "#B7A99A", fontSize: 12 }}>
            {showFrames ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {mm ? "Frame Details" : "פרטי פריימים"} ({result.frames.length})
          </button>
          {showFrames && (
            <div style={{ marginTop: 8, maxHeight: 180, overflowY: "auto", background: "rgba(18,15,13,0.6)", borderRadius: 10, padding: 10 }}>
              {result.frames.filter(f => f.deepfake > 1 || f.ai_generated > 1).map((f, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid rgba(74,63,55,0.4)", fontSize: 11, color: "#B7A99A" }}>
                  <span>{mm ? "Second" : "שנייה"} {f.timestamp}</span>
                  {f.deepfake > 1 && <span style={{ color: "#E53E3E" }}>Deepfake: {f.deepfake}%</span>}
                  {f.ai_generated > 1 && <span style={{ color: "#DD6B20" }}>AI: {f.ai_generated}%</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DemoCard({ item, onDragStart, onSelect }) {
  const { mamdaniMode: mm } = useMamdani();
  const vCfg = mm ? VERDICT_CONFIG_EN : VERDICT_CONFIG;
  const cfg = vCfg[item.verdict] || vCfg.clean;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={() => onSelect(item)}
      style={{
        background: "#26211D", border: "1px solid #4A3F37",
        borderRadius: 12, padding: 14, cursor: "grab",
        transition: "all 0.2s", userSelect: "none"
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = cfg.color; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#4A3F37"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {/* Preview */}
      <div style={{ width: "100%", height: 80, borderRadius: 8, overflow: "hidden", marginBottom: 10, background: "rgba(18,15,13,0.6)", position: "relative" }}>
        {item.type === "image" ? (
          <img src={item.media_url} alt={item.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <video src={item.media_url} muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        <div style={{ position: "absolute", top: 6, right: 6, background: cfg.bg, border: `1px solid ${cfg.color}50`, borderRadius: 6, padding: "2px 7px" }}>
          <span style={{ color: cfg.color, fontSize: 10, fontWeight: 700 }}>
            {item.verdict === "deepfake" ? `${item.deepfake_score}%` : `${item.ai_generated_score}%`}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ color: "#F3E7D7", fontSize: 12, fontWeight: 600 }}>{item.label}</span>
        {item.type === "video" ? <Film size={12} color="#B7A99A" /> : <ImageIcon size={12} color="#B7A99A" />}
      </div>
      <div style={{ color: cfg.color, fontSize: 11, marginTop: 3 }}>{cfg.label}</div>
      <div style={{ color: "rgba(74,63,55,0.7)", fontSize: 10, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
        <span>⠿</span> {mm ? "Drag to scan area" : "גרור לאזור הסריקה"}
      </div>
    </div>
  );
}

export default function DeepfakeScanner() {
  const { mamdaniMode: mm } = useMamdani();
  const [mode, setMode] = useState("upload");
  const [url, setUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState("");
  const [demoScanning, setDemoScanning] = useState(false);
  const [demoStep, setDemoStep] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const dragDemo = useRef(null);

  const handleScan = useCallback(async (file, mediaUrl, demoData) => {
    if (demoData) {
      setResult(null);
      setDemoScanning(true);
      setDemoStep(mm ? "Connecting to server..." : "מתחבר לשרת...");
      await new Promise(r => setTimeout(r, 1000));
      setDemoStep(mm ? "Sending for Hive AI analysis..." : "שולח לניתוח Hive AI...");
      await new Promise(r => setTimeout(r, 1200));
      setDemoStep(mm ? "Processing frames..." : "מעבד פריימים...");
      await new Promise(r => setTimeout(r, 1000));
      setDemoStep(mm ? "Claude generating analysis..." : "Claude מייצר ניתוח...");
      await new Promise(r => setTimeout(r, 800));
      setDemoScanning(false);
      setDemoStep("");
      setResult({ ...demoData, animate: true });
      return;
    }

    setScanning(true);
    setResult(null);
    setError(null);
    setScanStep(mm ? "Connecting to server..." : "מתחבר לשרת...");

    try {
      let data;
      const isVideo = file ? file.type.startsWith("video/") : /\.(mp4|webm|mov|m4v)$/i.test(mediaUrl || "");

      setScanStep(mm ? "Sending for Hive AI analysis..." : "שולח לניתוח Hive AI...");
      await new Promise(r => setTimeout(r, 400));

      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        setScanStep(mm ? "Processing frames..." : "מעבד פריימים...");

        const res = await fetch(`${API_BASE}/detect-file`, { method: "POST", body: formData });
        data = await res.json();

        if (data.error) throw new Error(data.error);

        setScanStep(mm ? "Analyzing results..." : "מנתח תוצאות...");

        const analyzeRes = await fetch(`${API_BASE}/analyze-scores`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            deepfake_score: data.deepfake_score || 0,
            ai_generated_score: data.ai_generated_score || 0,
            verdict: data.verdict || "clean",
            frames: data.frames || [],
            media_type: isVideo ? "video" : "image"
          })
        });

        const analyzeData = await analyzeRes.json();

        setResult({
          ...data,
          type: isVideo ? "video" : "image",
          media_url: URL.createObjectURL(file),
          analysis: analyzeData.analysis
        });

      } else {
        setScanStep(mm ? "Scanning content..." : "סורק תוכן...");
        const res = await fetch(`${API_BASE}/analyze`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ media_url: mediaUrl })
        });
        data = await res.json();

        setScanStep(mm ? "Claude generating analysis..." : "Claude מייצר ניתוח...");
        await new Promise(r => setTimeout(r, 300));

        if (data.error) throw new Error(data.error);

        setResult({
          ...data,
          type: isVideo ? "video" : "image",
          media_url: !isVideo ? mediaUrl : null,
        });
      }

    } catch (e) {
      setError(e.message);
    } finally {
      setScanning(false);
      setScanStep("");
    }
  }, [mm]);

  const handleFile = (file) => { if (file) handleScan(file, null, null); };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (dragDemo.current) {
      handleScan(null, null, dragDemo.current);
      dragDemo.current = null;
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const demoCases = mm ? DEMO_CASES_MAMDANI : DEMO_CASES;

  return (
    <div style={{ minHeight: "100vh", background: "transparent", padding: "28px 24px", direction: mm ? "ltr" : "rtl", fontFamily: "'Segoe UI', Tahoma, sans-serif" }}>

      <div style={{ marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg, #C8904A, #D89B5A)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ScanFace size={20} color="white" />
          </div>
          <h1 style={{ margin: 0, color: "#F3E7D7", fontSize: 22, fontWeight: 700 }}>{mm ? "Deepfake Detection" : "זיהוי Deepfake"}</h1>
        </div>
        <p style={{ color: "#B7A99A", margin: 0, fontSize: 13 }}>
          {mm
            ? "Upload an image or video for AI-based deepfake and synthetic content detection"
            : "העלה תמונה או וידאו לבדיקה מבוססת AI – זיהוי Deepfake, תוכן מזויף ופנים מוחלפות"}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>

        {/* אזור סריקה */}
        <div>
          {/* Mode Toggle */}
          <div style={{ display: "flex", gap: 4, background: "#26211D", borderRadius: 10, padding: 4, marginBottom: 14, width: "fit-content" }}>
            {[{ id: "upload", label: mm ? "Upload File" : "העלאת קובץ", icon: Upload }, { id: "url", label: mm ? "Enter URL" : "הזנת URL", icon: Link2 }].map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setMode(id)} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: mode === id ? "linear-gradient(135deg, #C8904A, #D89B5A)" : "none",
                border: "none", borderRadius: 8,
                padding: "7px 14px", cursor: "pointer",
                color: mode === id ? "white" : "#B7A99A",
                fontSize: 13, fontWeight: mode === id ? 600 : 400, transition: "all 0.2s"
              }}>
                <Icon size={14} />{label}
              </button>
            ))}
          </div>

          {/* Upload Zone */}
          {mode === "upload" && (
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => !scanning && fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? "#D89B5A" : "#4A3F37"}`,
                borderRadius: 14, padding: "40px 24px", textAlign: "center",
                cursor: scanning ? "default" : "pointer", transition: "all 0.2s",
                background: dragging ? "rgba(216,155,90,0.06)" : "#26211D",
              }}
            >
              <input ref={fileRef} type="file" accept="image/*,video/*,.mp4,.webm,.mov,.jpg,.jpeg,.png" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
              {scanning ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <div style={{ position: "relative", width: 56, height: 56 }}>
                    <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid #D89B5A", borderTopColor: "transparent", animation: "spin 1s linear infinite" }} />
                    <div style={{ position: "absolute", inset: 8, borderRadius: "50%", border: "2px solid #E53E3E", borderTopColor: "transparent", animation: "spin 0.6s linear infinite reverse" }} />
                    <ScanFace size={18} color="#D89B5A" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
                  </div>
                  <div style={{ color: "#D89B5A", fontSize: 14, fontWeight: 600 }}>{scanStep}</div>
                  <div style={{ color: "rgba(74,63,55,0.7)", fontSize: 12 }}>{mm ? "Analyzing content with AI..." : "מנתח תוכן עם AI..."}</div>
                </div>
              ) : (
                <>
                  <Upload size={32} color={dragging ? "#D89B5A" : "#B7A99A"} style={{ marginBottom: 10 }} />
                  <div style={{ color: "#F3E7D7", fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                    {dragging
                      ? (mm ? "Drop here to scan" : "שחרר כאן לסריקה")
                      : (mm ? "Drag & drop or click to upload" : "גרור ושחרר או לחץ להעלאה")}
                  </div>
                  <div style={{ color: "#B7A99A", fontSize: 12 }}>MP4, MOV, JPG, PNG • {mm ? "Max 200MB" : "מקסימום 200MB"}</div>
                  <div style={{ color: "rgba(74,63,55,0.8)", fontSize: 11, marginTop: 8 }}>{mm ? "→ Drag a sample from the right" : "ניתן גם לגרור דוגמה מהצד ←"}</div>
                </>
              )}
            </div>
          )}

          {/* URL Input */}
          {mode === "url" && (
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                style={{ flex: 1, background: "#26211D", border: "1px solid #4A3F37", borderRadius: 10, padding: "11px 14px", color: "#F3E7D7", caretColor: "#D89B5A", fontSize: 13, outline: "none", direction: "ltr" }}
                onKeyDown={e => e.key === "Enter" && url && handleScan(null, url, null)}
              />
              <button onClick={() => url && handleScan(null, url, null)} disabled={scanning} style={{ background: scanning ? "rgba(74,63,55,0.4)" : "linear-gradient(135deg, #C8904A, #D89B5A)", border: "none", borderRadius: 10, padding: "11px 18px", color: "white", cursor: scanning ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>
                {scanning ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : (mm ? "Scan" : "סרוק")}
              </button>
            </div>
          )}

          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

          {error && (
            <div style={{ background: "rgba(229,62,62,0.1)", border: "1px solid rgba(229,62,62,0.3)", borderRadius: 12, padding: 14, marginTop: 14, color: "#FC8181", fontSize: 13 }}>❌ {error}</div>
          )}

          {demoScanning && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: 28, padding: "32px 0" }}>
              <div style={{ position: "relative", width: 56, height: 56 }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid #D89B5A", borderTopColor: "transparent", animation: "spin 1s linear infinite" }} />
                <div style={{ position: "absolute", inset: 8, borderRadius: "50%", border: "2px solid #E53E3E", borderTopColor: "transparent", animation: "spin 0.6s linear infinite reverse" }} />
                <ScanFace size={18} color="#D89B5A" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
              </div>
              <div style={{ color: "#D89B5A", fontSize: 14, fontWeight: 600 }}>{demoStep}</div>
              <div style={{ color: "rgba(74,63,55,0.7)", fontSize: 12 }}>{mm ? "Analyzing content with AI..." : "מנתח תוכן עם AI..."}</div>
            </div>
          )}

          {result && !scanning && !demoScanning && (
            <ResultCard result={result} onClose={() => setResult(null)} />
          )}
        </div>

        {/* דוגמאות */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <Zap size={13} color="#D89B5A" />
            <span style={{ color: "#B7A99A", fontSize: 12, fontWeight: 600 }}>{mm ? "Sample Cases" : "דוגמאות לבדיקה"}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {demoCases.map(item => (
              <DemoCard
                key={item.id}
                item={item}
                onDragStart={() => { dragDemo.current = item; }}
                onSelect={(demo) => handleScan(null, null, demo)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
